import {
	Button,
	ChipInput,
	ChipSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { smartDistribution } from "~/core/api/smart-distribution";
import { dateTime } from "~/core/format";
import { lookup, pick, useI18n, useT } from "~/core/i18n";
import { Async } from "~/core/ui/states";

export function DistributionTasks({
	brandId,
	brands,
}: {
	brandId: string;
	brands: { entity_id: string; name: string }[];
}) {
	const { locale } = useI18n();
	const t = useT();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const [brand, setBrand] = useState("");
	const availableBrands = brands.length ? brands : [{ entity_id: brandId, name: tr("Current brand", "当前品牌") }];
	const [status, setStatus] = useState("");
	const [kind, setKind] = useState("");
	const [params] = useSearchParams();
	const [stopping, setStopping] = useState<string | null>(null);
	const [actionError, setActionError] = useState(false);
	const [search, setSearch] = useState(() => params.get("operation") ?? "");
	const state = useQuery({
		queryKey: [availableBrands.map((b) => b.entity_id), brand, "distribution-operations"],
		queryFn: async () =>
			(
				await Promise.all(
					availableBrands
						.filter((b) => !brand || b.entity_id === brand)
						.map(async (b) =>
							(
								await smartDistribution.operations(b.entity_id)
							).map((row) => ({ ...row, entity_id: b.entity_id, brand_name: b.name })),
						),
				)
			)
				.flat()
				.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)),
		refetchInterval: 3000,
	});
	return (
		<div className="flex min-h-0 flex-col gap-4">
			<div className="flex flex-wrap items-center gap-2">
				<ChipSelect
					aria-label={tr("Brand", "品牌")}
					placeholder={tr("All brands", "全部品牌")}
					value={brand}
					onChange={setBrand}
					options={[
						{ value: "", label: tr("All brands", "全部品牌") },
						...availableBrands.map((b) => ({ value: b.entity_id, label: b.name })),
					]}
				/>
				<ChipSelect
					aria-label={tr("Task type", "任务类型")}
					placeholder={tr("All types", "全部类型")}
					value={kind}
					onChange={setKind}
					options={[
						{ value: "", label: tr("All types", "全部类型") },
						{ value: "recommend", label: tr("Recommend channels", "推荐渠道") },
						{ value: "adapt", label: tr("Generate content", "生成内容") },
						{ value: "publish", label: tr("Publish content", "执行分发") },
					]}
				/>
				<ChipSelect
					aria-label={tr("Status", "状态")}
					placeholder={tr("All statuses", "全部状态")}
					value={status}
					onChange={setStatus}
					options={[
						{ value: "", label: tr("All statuses", "全部状态") },
						...(
							[
								"queued",
								"running",
								"completed",
								"failed",
								"waiting_human",
								"published",
								"remote_draft",
								"unknown",
								"cancelled",
							] as const
						).map((value) => ({
							value,
							label: tr(
								{
									queued: "Queued",
									running: "Running",
									completed: "Completed",
									failed: "Failed",
									waiting_human: "Waiting for verification",
									published: "Published",
									remote_draft: "Remote draft",
									unknown: "Result unknown",
									cancelled: "Cancelled",
								}[value],
								{
									queued: "排队中",
									running: "执行中",
									completed: "已完成",
									failed: "失败",
									waiting_human: "等待验证",
									published: "已发布",
									remote_draft: "平台草稿",
									unknown: "结果未知",
									cancelled: "已取消",
								}[value],
							),
						})),
					]}
				/>
				<ChipInput
					className="w-60 max-w-full"
					aria-label={tr("Search tasks", "搜索任务")}
					placeholder={tr("Search task ID or material", "搜索任务编号或素材")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Button variant="ghost" onClick={() => void state.refetch()}>
					{tr("Refresh", "刷新")}
				</Button>
			</div>
			{actionError && (
				<p role="alert" className="text-destructive">
					{tr("Could not stop the task. Refresh its status before trying again.", "停止任务失败，请刷新状态后再操作。")}
				</p>
			)}
			<Async state={state}>
				{(rows) => {
					const filtered = rows.filter(
						(row) =>
							(!status || row.state === status) &&
							(!kind || row.kind === kind) &&
							(!search ||
								`${row.id} ${row.brief_id} ${row.materials.map((m) => m.title).join(" ")}`
									.toLowerCase()
									.includes(search.toLowerCase())),
					);
					return (
						<div className="overflow-x-auto">
							<Table className="min-w-[660px] table-fixed [&_td]:whitespace-normal">
								<TableHeader>
									<TableRow>
										<TableHead className="w-[29%]">{tr("Task", "任务")}</TableHead>
										<TableHead className="w-[24%]">{tr("Progress", "进度")}</TableHead>
										<TableHead className="w-[13%]">{tr("Source", "来源")}</TableHead>
										<TableHead className="w-[20%]">{tr("Created", "创建时间")}</TableHead>
										<TableHead className="w-[14%]">{tr("Actions", "操作")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{filtered.map((row) => (
										<TableRow key={row.id}>
											<TableCell>
												<strong>
													{row.kind === "recommend"
														? tr("Recommend channels", "推荐渠道")
														: row.kind === "publish"
															? tr("Publish content", "执行分发")
															: tr("Generate channel content", "生成渠道内容")}
												</strong>
												<div className="text-muted-foreground break-words text-xs" title={row.id}>
													#{row.id.slice(-8)} · {row.platform_id ? `${row.platform_id} · ` : ""}
													{row.brand_name} · {row.materials[0]?.title}
												</div>
											</TableCell>
											<TableCell>
												{row.kind === "publish"
													? t(`smartDistribution.state.${row.state}`)
													: row.state === "completed"
														? tr("Completed", "已完成")
														: t(`smartDistribution.stage.${row.stage}`)}
												{row.state === "unknown" && (
													<div className="text-destructive text-xs">
														{tr(
															"Verify the platform result before publishing again.",
															"请先核实平台结果，勿重复发布。",
														)}
													</div>
												)}
												{row.error && (
													<div className="text-destructive break-words text-xs">
														{lookup(
															locale,
															`errors.distribution_${row.error === "invalid_distribution_input" ? "invalid_input" : row.error}`,
														) ||
															(row.kind === "publish"
																? tr(
																		"Publishing failed. Open the plan to inspect the task.",
																		"分发失败，请打开计划查看任务。",
																	)
																: t("smartDistribution.error"))}
													</div>
												)}
											</TableCell>
											<TableCell>
												{row.source?.kind === "workflow"
													? tr("Workflow node", "工作流节点")
													: tr("Manual action", "手动操作")}
												{row.source?.run_id && (
													<div className="text-muted-foreground text-xs" title={row.source.run_id}>
														#{row.source.run_id.slice(-8)}
													</div>
												)}
											</TableCell>
											<TableCell>
												{dateTime(locale, row.created_at)}
												{row.scheduled_at && (
													<div className="text-muted-foreground text-xs">
														{tr("Scheduled", "排期")} {dateTime(locale, row.scheduled_at)}
													</div>
												)}
												{row.started_at && (
													<div className="text-muted-foreground text-xs">
														{tr("Elapsed", "已耗时")}{" "}
														{Math.max(
															0,
															Math.floor(
																(Date.parse(row.finished_at || new Date().toISOString()) - Date.parse(row.started_at)) /
																	1000,
															),
														)}{" "}
														{tr("s", "秒")}
													</div>
												)}
											</TableCell>
											<TableCell>
												{row.materials[0] && (
													<>
														<Link
															className="text-primary whitespace-nowrap"
															to={`/w/${encodeURIComponent(row.entity_id)}/agent/home?${new URLSearchParams({ article: row.materials[0].id, resource: row.materials[0].id, distribution_brief: row.brief_id })}`}
														>
															{tr("View plan", "查看计划")}
														</Link>
														{row.kind === "publish" && ["queued", "running", "waiting_human"].includes(row.state) && (
															<Button
																className="h-[var(--control-h-sm)]"
																variant="ghost"
																disabled={stopping === row.id}
																onClick={async () => {
																	setStopping(row.id);
																	setActionError(false);
																	try {
																		const brief = await smartDistribution.read(row.entity_id, row.brief_id);
																		await smartDistribution.change(row.entity_id, row.brief_id, "cancel", {
																			revision: brief.revision,
																			job_id: row.id,
																		});
																		await state.refetch();
																	} catch {
																		setActionError(true);
																	} finally {
																		setStopping(null);
																	}
																}}
															>
																{tr("Stop", "停止")}
															</Button>
														)}
													</>
												)}
											</TableCell>
										</TableRow>
									))}
									{!filtered.length && (
										<TableRow>
											<TableCell colSpan={5}>{tr("No intelligent distribution tasks", "暂无智能分发任务")}</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>
					);
				}}
			</Async>
		</div>
	);
}
